import { z } from "zod";
import { ADMIN_GROUP_ROLE_REASON_MAX_LENGTH } from "./admin-group-roles.js";
import { GROUP_SUSPENDED_CODE } from "./admin-group-lifecycle.js";

/**
 * Shared DTO — SUPER_ADMIN duyệt vào nhóm + gắn đợt thu (US-AE4.F6-0858):
 *  - `@UC9876` hàng chờ (gộp HAI hàng của app) + xem trước khoản sẽ nợ
 *  - `@UC9877` duyệt vào nhóm, kèm tick đợt thu "chọn người"
 *  - `@UC9878` từ chối
 *  - `@UC9879` gắn thành viên hiện có vào đợt thu "chọn người"
 *
 * App có HAI hàng chờ khác bảng (story §1): `join_request` = dòng `join_requests` (chưa có suất) ·
 * `seat` = suất `join_pending` (link mở, người quay lại, phụ huynh chọn suất bé). `requestId` là id của
 * dòng tương ứng — `kind` nói phải tra bảng nào, nên hai loại id không bao giờ bị nhầm với nhau.
 *
 * 🔴 Tiền trên wire là STRING minor (JSON không có int64 an toàn). Tổng "Nợ ngay" do SERVER tính
 * (`nowTotalMinor`) — UI không cộng tiền; tick đổi ⇒ hỏi lại preview.
 */

const reasonSchema = z.string().trim().min(1).max(ADMIN_GROUP_ROLE_REASON_MAX_LENGTH);
/** Tiền minor ≥ 0 dạng chuỗi số nguyên (response — 0 là giá trị thật, vd "còn thiếu 0đ"). */
const minorSchema = z.string().regex(/^\d+$/);

/** Trần số đợt tick trong một lần — chặn body khổng lồ, dư xa số đợt đang mở của một nhóm thật. */
export const ADMIN_JOIN_QUEUE_PICK_MAX = 50;

const uuidList = z
  .array(z.string().uuid())
  .max(ADMIN_JOIN_QUEUE_PICK_MAX)
  .refine((ids) => new Set(ids.map((i) => i.toLowerCase())).size === ids.length, { message: "Trùng đợt thu" });

export const adminJoinQueueKindSchema = z.enum(["join_request", "seat"]);
export type AdminJoinQueueKind = z.infer<typeof adminJoinQueueKindSchema>;

/** Người đó đến từ đâu: bấm "xin vào" · bấm link mời · phụ huynh chọn suất bé. */
export const adminJoinQueueViaSchema = z.enum(["join_request", "invite_link", "child_seat"]);
export type AdminJoinQueueVia = z.infer<typeof adminJoinQueueViaSchema>;

export const adminJoinQueueItemSchema = z
  .object({
    kind: adminJoinQueueKindSchema,
    requestId: z.string(),
    via: adminJoinQueueViaSchema,
    /** Tên người xin (null = không rõ). */
    displayName: z.string().nullable(),
    /** Tên suất bé khi `via='child_seat'`; còn lại null. */
    seatDisplayName: z.string().nullable(),
    requestedAt: z.string(),
    /** Có suất cũ trong nhóm (từng ở nhóm rồi rời) — khoản đã đóng trên suất cũ được giữ nguyên. */
    returning: z.boolean(),
  })
  .strict();
export type AdminJoinQueueItem = z.infer<typeof adminJoinQueueItemSchema>;

export const adminJoinQueueListResponseSchema = z.object({
  data: z.array(adminJoinQueueItemSchema),
  pageInfo: z.object({ hasNext: z.boolean(), nextCursor: z.string().nullable() }),
});
export type AdminJoinQueueListResponse = z.infer<typeof adminJoinQueueListResponseSchema>;

/**
 * Vì sao nhóm bị chặn thao tác ghi — nhân viên dùng để báo lại người dùng. US-AE4.F7-1010 (QĐ người dùng 06/10/2026): mã HOA của app,
 * giống `GroupRoleGuard` của duyệt vào nhóm — chỉ nhóm đình chỉ. Nhóm đã xoá ⇒ 404; nhóm lưu trữ / quỹ đóng băng không chặn vào nhóm.
 */
export const adminJoinBlockedSchema = z.enum([GROUP_SUSPENDED_CODE]);
export type AdminJoinBlocked = z.infer<typeof adminJoinBlockedSchema>;

/** Vì sao một đợt thu không tính: đã kết thúc · không chia đều (QĐ-5) · đã ở trong lịch/đợt từ trước. */
export const adminJoinSkipReasonSchema = z.enum(["closed", "unsupported_mode", "already_in"]);
export type AdminJoinSkipReason = z.infer<typeof adminJoinSkipReasonSchema>;

const autoItem = z.object({ collectionId: z.string(), title: z.string(), amountMinor: minorSchema }).strict();
const pickOnceItem = z
  .object({ collectionId: z.string(), title: z.string(), amountMinor: minorSchema, picked: z.boolean() })
  .strict();
const pickRecurringItem = z.object({ scheduleId: z.string(), name: z.string(), picked: z.boolean() }).strict();
const existingItem = z
  .object({
    collectionId: z.string(),
    title: z.string(),
    amountMinor: minorSchema,
    /** Phần ĐÃ xác nhận nhận (lời khai chờ đối chiếu KHÔNG tính — §3.7). */
    paidMinor: minorSchema,
    outstandingMinor: minorSchema,
    status: z.string(),
  })
  .strict();
const skippedItem = z.object({ collectionId: z.string(), title: z.string(), reason: adminJoinSkipReasonSchema }).strict();

const previewCore = {
  blocked: adminJoinBlockedSchema.nullable(),
  currency: z.string(),
  /** Đợt "chọn người" 1 lần đang mở — tick ⇒ nợ ngay. */
  pickOnce: z.array(pickOnceItem),
  /** Lịch định kỳ "chọn người" đang chạy — tick ⇒ tính từ kỳ sau (QĐ-4: không hiện số tiền theo kỳ). */
  pickRecurring: z.array(pickRecurringItem),
  /** Khoản ĐANG có trên suất (người quay lại / suất bé / thành viên hiện có). */
  existing: z.array(existingItem),
  skipped: z.array(skippedItem),
  /** Tổng khoản MỚI 1 lần sẽ phát sinh nếu bấm ngay bây giờ (tự tính + đã tick). */
  nowTotalMinor: minorSchema,
};

export const adminJoinPreviewResponseSchema = z
  .object({
    kind: adminJoinQueueKindSchema,
    requestId: z.string(),
    /** Suất sẽ được kích hoạt (null = người mới, suất tạo lúc duyệt). */
    targetMemberId: z.string().nullable(),
    displayName: z.string().nullable(),
    via: adminJoinQueueViaSchema,
    returning: z.boolean(),
    /** Đợt "toàn bộ" 1 lần đang mở — tự tính khi duyệt (cùng vị-từ với auto-enroll). */
    auto: z.array(autoItem),
    /** Lịch định kỳ "toàn bộ" — tự tính từ kỳ sau, không cần chọn. */
    autoRecurring: z.array(z.object({ scheduleId: z.string(), name: z.string() }).strict()),
    ...previewCore,
  })
  .strict();
export type AdminJoinPreviewResponse = z.infer<typeof adminJoinPreviewResponseSchema>;

export const adminCollectionAssignPreviewResponseSchema = z
  .object({ groupMemberId: z.string(), ...previewCore })
  .strict();
export type AdminCollectionAssignPreviewResponse = z.infer<typeof adminCollectionAssignPreviewResponseSchema>;

/** Query preview: `?collectionIds=a,b&scheduleIds=c` — các đợt đang tick. */
const csvUuids = z
  .string()
  .optional()
  .transform((v) => (v ? v.split(",").map((s) => s.trim()).filter(Boolean) : []))
  .pipe(uuidList);
export const adminJoinPreviewQuerySchema = z.object({ collectionIds: csvUuids, scheduleIds: csvUuids }).strict();
export type AdminJoinPreviewQuery = z.infer<typeof adminJoinPreviewQuerySchema>;

const picks = { collectionIds: uuidList.default([]), scheduleIds: uuidList.default([]) };
const support = { reason: reasonSchema, supportTicketId: z.string().uuid().optional() };

/** Body POST …/join-queue/:kind/:requestId/approve. Người duyệt lấy từ phiên admin, không từ body. */
export const adminApproveJoinBodySchema = z.object({ ...support, ...picks }).strict();
export type AdminApproveJoinBody = z.infer<typeof adminApproveJoinBodySchema>;

/** Body POST …/join-queue/:kind/:requestId/reject. */
export const adminRejectJoinBodySchema = z.object(support).strict();
export type AdminRejectJoinBody = z.infer<typeof adminRejectJoinBodySchema>;

/** Body POST …/members/:memberId/collection-assign — chỉ GẮN THÊM (QĐ-8). */
export const adminAssignCollectionsBodySchema = z
  .object({ ...support, ...picks })
  .strict()
  .refine((b) => b.collectionIds.length + b.scheduleIds.length > 0, { message: "Chọn ít nhất một đợt thu" });
export type AdminAssignCollectionsBody = z.infer<typeof adminAssignCollectionsBodySchema>;

const createdDue = z.object({ collectionId: z.string(), amountMinor: minorSchema }).strict();

export const adminApproveJoinResponseSchema = z
  .object({
    groupMemberId: z.string(),
    createdDues: z.array(createdDue),
    addedScheduleIds: z.array(z.string()),
    nowTotalMinor: minorSchema,
  })
  .strict();
export type AdminApproveJoinResponse = z.infer<typeof adminApproveJoinResponseSchema>;

export const adminRejectJoinResponseSchema = z
  .object({ kind: adminJoinQueueKindSchema, requestId: z.string(), status: z.literal("rejected") })
  .strict();
export type AdminRejectJoinResponse = z.infer<typeof adminRejectJoinResponseSchema>;

export const adminAssignCollectionsResponseSchema = adminApproveJoinResponseSchema;
export type AdminAssignCollectionsResponse = AdminApproveJoinResponse;

/** Mã lỗi ổn định (problem+json `code`). */
export const ADMIN_JOIN_QUEUE_CODE = {
  /**
   * Nhóm đang ĐÌNH CHỈ — `params.blocked` nói lý do. US-AE4.F7-1010 (QĐ người dùng 06/10/2026, giống app `GroupRoleGuard`): nhóm lưu trữ
   * / quỹ đóng băng KHÔNG chặn vào nhóm; nhóm đã xoá ⇒ 404 (không phải mã này).
   */
  GROUP_BLOCKED: "ADMIN_JOIN_QUEUE_GROUP_BLOCKED",
  /** Yêu cầu đã được xử lý (duyệt / từ chối) hoặc không còn ở hàng chờ. */
  REQUEST_NOT_PENDING: "ADMIN_JOIN_QUEUE_REQUEST_NOT_PENDING",
  /** Một đợt tick không (còn) đủ điều kiện gắn — không đổi gì cả. */
  PICK_NOT_ELIGIBLE: "ADMIN_JOIN_QUEUE_PICK_NOT_ELIGIBLE",
  /** Thành viên không ở trạng thái gắn được đợt thu. */
  MEMBER_NOT_ASSIGNABLE: "ADMIN_JOIN_QUEUE_MEMBER_NOT_ASSIGNABLE",
  /** Người khác vừa xử lý cùng yêu cầu — tải lại. */
  CONCURRENT_CHANGE: "ADMIN_JOIN_QUEUE_CONCURRENT_CHANGE",
} as const;
